import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers";
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
} from "@dnd-kit/sortable";
import { mergeClasses } from "@fluentui/react-components";
import {
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  SortingState,
  Updater,
  useReactTable,
} from "@tanstack/react-table";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { StaticGlobalFilterContext } from "../../../../context/StaticGlobalFilterContext";
import { FlightLegTypeEnum } from "../../../../enum/FlightsEnum";
import { ITableState } from "../../../../models/flights/IFlightsCommon";
import { getAirportConfig } from "../../../../utils/common/CommonHelperFunctions";
import { EMPTY_STATE_MESSAGE } from "../../../../utils/common/Constants";
import { getConvertedTime } from "../../../../utils/common/DateHelperFunctions";
import {
  getFlightRowUniqueKey,
  getSelectedRowIndexByUniqueKey,
  getTableRowClass,
  isColumnFilterActive,
  isColumnSortActive,
} from "../../../../utils/flights/flight-table/FlightTableHelper";
import { NoDataCard } from "../../../common/no-data-card/NoDataCard";
import { enhanceFlightColumnConfig } from "./column-config/injectCustomColumnLogic";
import { DraggableTableHeader } from "./DraggableTableHeader";
import { useRowStyles } from "./tableRowStyles";
import { useTableStyles } from "./tableStyles";
const columnResizeDirection = "ltr";

export interface IFlightDetailsTableProps {
  airportCode: string;
  flighData: any[];
  tableColumns: any[];
  tableContext: { legType: FlightLegTypeEnum; activeState: ITableState };
  onStateChange: (
    leg: FlightLegTypeEnum,
    filtersUpdater: Updater<ColumnFiltersState>,
    sortingUpdater: Updater<SortingState>
  ) => void;
  currentFlightRowIndex?: number;
}

export default function FlightDetailsTable({
  airportCode,
  flighData,
  tableColumns: columns,
  tableContext: { legType: selectedLegType, activeState },
  onStateChange,
}: IFlightDetailsTableProps) {
  const styles = useTableStyles();
  const rowStyles = useRowStyles();

  const [columnOrder, setColumnOrder] = useState<string[]>(() =>
    columns.map((c) => c.id ?? "")
  );
  const [selectedRow, setSelectedRow] = useState<number | null>(0);

  const { timezone } = useContext(StaticGlobalFilterContext);
  const { timeZoneId: newAirportTimeZone } = getAirportConfig(airportCode);
  const currentAirportTime = getConvertedTime(
    new Date(),
    timezone,
    newAirportTimeZone
  );

  const tableRef = useRef<HTMLDivElement>(null);
  const selectedFlightKeyRef = useRef<string | null>(
    getFlightRowUniqueKey(flighData[0])
  );

  const enhancedColumns = useMemo(() => {
    return enhanceFlightColumnConfig(columns, airportCode, currentAirportTime);
  }, [columns, currentAirportTime]);

  // Rows should be highlighted only for Ariival or Departure leg types
  const isRowHighlighted = selectedLegType !== FlightLegTypeEnum.Turnaround;

  const handleRowClick = useCallback(
    (event: React.MouseEvent<HTMLTableRowElement>) => {
      const indexAttr = event.currentTarget.getAttribute("data-index");
      const selectedFlightKey = event.currentTarget.getAttribute(
        "data-flight-row-key"
      );
      const rowIndex = indexAttr ? parseInt(indexAttr, 10) : -1;

      if (!isNaN(rowIndex)) {
        setSelectedRow(rowIndex);
        selectedFlightKeyRef.current = selectedFlightKey;
      }
    },
    []
  );

  useEffect(() => {
    setColumnOrder(() => columns.map((c) => c.id ?? ""));
  }, [columns]);

  const table = useReactTable({
    data: flighData,
    columns: enhancedColumns,
    state: {
      columnOrder,
      sorting: activeState.sorting,
      columnFilters: activeState.filters,
    },
    columnResizeMode: "onChange",
    columnResizeDirection,
    filterFns: {},
    onColumnOrderChange: setColumnOrder,
    getFilteredRowModel: getFilteredRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: (filtersUpdater) => {
      onStateChange(
        selectedLegType,
        filtersUpdater,
        table.getState().sorting // current sorting array from table
      );
    },
    onSortingChange: (sortingUpdater) => {
      onStateChange(
        selectedLegType,
        table.getState().columnFilters, // current filters array from table
        sortingUpdater
      );
    },
  });

  useEffect(() => {
    // Retain row selection
    const currentRowsData = table.getRowModel().rows.map((row) => row.original);
    const newSelectedRowIndex = getSelectedRowIndexByUniqueKey(
      currentRowsData,
      selectedFlightKeyRef.current
    );

    setSelectedRow(newSelectedRowIndex);
    selectedFlightKeyRef.current = getFlightRowUniqueKey(
      currentRowsData[newSelectedRowIndex]
    );
  }, [flighData, activeState.filters, activeState.sorting]);

  useEffect(() => {
    // Reset selected row when leg type or filters/sorting change
    setSelectedRow(0);
    const firstVisibleRow = table.getRowModel().rows[0]?.original;
    selectedFlightKeyRef.current = getFlightRowUniqueKey(firstVisibleRow);

    if (tableRef.current) {
      tableRef.current.scrollTo({
        top: 0,
      });
    }
  }, [selectedLegType, activeState.filters, activeState.sorting]);

  // reorder columns after drag & drop
  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      setColumnOrder((columnOrder) => {
        const oldIndex = columnOrder.indexOf(active.id as string);
        const newIndex = columnOrder.indexOf(over.id as string);
        return arrayMove(columnOrder, oldIndex, newIndex); //this is just a splice util
      });
    }
  }

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {})
  );

  return (
    <DndContext
      collisionDetection={closestCenter}
      modifiers={[restrictToHorizontalAxis]}
      onDragEnd={handleDragEnd}
      sensors={sensors}
    >
      <div
        className={styles.tableContainer}
        ref={tableRef}
        style={{ direction: columnResizeDirection }}
        data-testid="flight-details-table-container"
      >
        {columns.length === 0 && (
          <NoDataCard message={EMPTY_STATE_MESSAGE.Data} />
        )}
        <table
          className={styles.table}
          style={{ width: table.getCenterTotalSize() }}
        >
          <thead className={mergeClasses(styles.thead, styles.stickyHeader)}>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                <SortableContext
                  items={columnOrder}
                  strategy={horizontalListSortingStrategy}
                >
                  {headerGroup.headers.map((header) => (
                    <DraggableTableHeader
                      key={header.id}
                      header={header}
                      isColumnFilterActive={isColumnFilterActive(
                        header.column.id,
                        activeState.filters
                      )}
                      isColumnSortActive={isColumnSortActive(
                        header.column.id,
                        activeState.sorting
                      )}
                    />
                  ))}
                </SortableContext>
              </tr>
            ))}
          </thead>
          <tbody className={mergeClasses(styles.tbody, "fw-500")}>
            {table.getRowModel().rows.map((row, rowIndex) => {
              let status;
              switch (selectedLegType) {
                case FlightLegTypeEnum.Arrival:
                  status = row.original?.arrival?.status;
                  break;
                case FlightLegTypeEnum.Departure:
                  status = row.original?.departure?.status;
                  break;
                case FlightLegTypeEnum.All:
                  status = row.original?.flightStatus;
                  break;
                default:
                  status = undefined;
              }

              const selectedRowStyle =
                selectedRow === rowIndex ? rowStyles.selectedRow : "";

              return (
                <tr
                  key={row.id}
                  data-index={rowIndex}
                  data-flight-row-key={getFlightRowUniqueKey(row.original)}
                  onClick={handleRowClick}
                  className={mergeClasses(
                    styles.trow,
                    selectedRowStyle,
                    rowStyles.baseRow,
                    isRowHighlighted && getTableRowClass(status, rowStyles)
                  )}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      id={cell.column.columnDef.id}
                      key={cell.id}
                      className={mergeClasses(styles.tdata, "fs-12", "pa-1")}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </DndContext>
  );
}
